import { createSlice, PayloadAction } from '@reduxjs/toolkit';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import axios from 'axios';
import config from '../config/config.json';
import toast from 'react-hot-toast';
import {
  CartItem,
  CartItemGroup,
  CartSummary,
  CartOrderRequest,
  groupCartItems,
  calculateCartSummary,
  createCartItemGroupKey,
} from '../models/cartItem';

interface CartItemsState {
  items: CartItem[];
  groupedItems: Record<string, CartItemGroup>;
  summary: CartSummary;
  isLoading: boolean;
  error: string | null;
  currency: 'fiat' | 'fg';
  gameDetails: {
    name: string;
    password: string;
    realm: 'europe' | 'americas' | 'asia';
  };
  isHalted: boolean;
}

const initialState: CartItemsState = {
  items: [],
  groupedItems: {},
  summary: {
    totalItems: 0,
    totalFiatPrice: '0',
    totalFgPrice: '0',
    currency: 'fiat',
  },
  isLoading: false,
  error: null,
  currency: 'fiat',
  gameDetails: {
    name: '',
    password: '',
    realm: 'europe',
  },
  isHalted: false,
};

const cartItemsSlice = createSlice({
  name: 'cartItems',
  initialState,
  reducers: {
    setCartItems: (state, action: PayloadAction<CartItem[]>) => {
      state.items = action.payload;
      state.groupedItems = groupCartItems(action.payload);
      state.summary = calculateCartSummary(action.payload, state.currency);
    },
    addCartItem: (state, action: PayloadAction<CartItem>) => {
      state.items.push(action.payload);
      state.groupedItems = groupCartItems(state.items);
      state.summary = calculateCartSummary(state.items, state.currency);
    },
    removeCartItem: (state, action: PayloadAction<number>) => {
      state.items = state.items.filter((item) => item.id !== action.payload);
      state.groupedItems = groupCartItems(state.items);
      state.summary = calculateCartSummary(state.items, state.currency);
    },
    removeCartItemsByGroup: (state, action: PayloadAction<string>) => {
      const groupKey = action.payload;
      const group = state.groupedItems[groupKey];
      if (group) {
        const itemIdsToRemove = group.items.map((item) => item.id);
        state.items = state.items.filter((item) => !itemIdsToRemove.includes(item.id));
        state.groupedItems = groupCartItems(state.items);
        state.summary = calculateCartSummary(state.items, state.currency);
      }
    },
    updateCartItemQuantity: (
      state,
      action: PayloadAction<{ itemId: number; quantity: number }>
    ) => {
      const { itemId, quantity } = action.payload;
      const targetItem = state.items.find((item) => item.id === itemId);

      if (targetItem) {
        const groupKey = createCartItemGroupKey(targetItem);
        const group = state.groupedItems[groupKey];

        if (group) {
          // Remove excess items if quantity is being reduced
          if (quantity < group.quantity) {
            const itemsToRemove = group.quantity - quantity;
            const itemIdsToRemove = group.items.slice(0, itemsToRemove).map((item) => item.id);
            state.items = state.items.filter((item) => !itemIdsToRemove.includes(item.id));
          }

          // Recalculate groupings and summary
          state.groupedItems = groupCartItems(state.items);
          state.summary = calculateCartSummary(state.items, state.currency);
        }
      }
    },
    clearCartItems: (state) => {
      state.items = [];
      state.groupedItems = {};
      state.summary = {
        totalItems: 0,
        totalFiatPrice: '0',
        totalFgPrice: '0',
        currency: state.currency,
      };
    },
    setCurrency: (state, action: PayloadAction<'fiat' | 'fg'>) => {
      state.currency = action.payload;
      state.summary = calculateCartSummary(state.items, action.payload);
    },
    setGameDetails: (state, action: PayloadAction<Partial<CartItemsState['gameDetails']>>) => {
      state.gameDetails = { ...state.gameDetails, ...action.payload };
    },
    setIsHalted: (state, action: PayloadAction<boolean>) => {
      state.isHalted = action.payload;
    },
    setLoading: (state, action: PayloadAction<boolean>) => {
      state.isLoading = action.payload;
    },
    setError: (state, action: PayloadAction<string | null>) => {
      state.error = action.payload;
    },
  },
});

export const {
  setCartItems,
  addCartItem,
  removeCartItem,
  removeCartItemsByGroup,
  updateCartItemQuantity,
  clearCartItems,
  setCurrency,
  setGameDetails,
  setIsHalted,
  setLoading,
  setError,
} = cartItemsSlice.actions;

export default cartItemsSlice.reducer;

// TanStack Query hooks
export function useCartItems() {
  const sessionToken = localStorage.getItem('session_token');
  return useQuery({
    queryKey: ['cartItems'],
    queryFn: async (): Promise<CartItem[]> => {
      const response = await axios.post(
        `${config.backend_base_url}cart/items/all`,
        { term: '', quick_cart: 1 },
        { headers: { 'session-token': sessionToken } }
      );
      return response.data || [];
    },
    enabled: !!sessionToken,
    refetchOnMount: true,
    refetchOnWindowFocus: true,
    retry: 2,
  });
}

export function useCartSummary() {
  const sessionToken = localStorage.getItem('session_token');
  return useQuery({
    queryKey: ['cartSummary'],
    queryFn: async () => {
      const response = await axios.get(`${config.backend_base_url}cart/mine`, {
        headers: { 'session-token': sessionToken },
      });
      return {
        total_fiat_price: response.data?.total_fiat_price || '0',
        total_fg_price: response.data?.total_fg_price || '0',
      };
    },
    enabled: !!sessionToken,
    refetchOnMount: true,
    refetchOnWindowFocus: true,
    retry: 2,
  });
}

export function useAddCartItem() {
  const queryClient = useQueryClient();
  const sessionToken = localStorage.getItem('session_token');

  return useMutation({
    mutationFn: async (itemIds: number[]) => {
      const response = await axios.post(
        `${config.backend_base_url}d2rs/cart/add`,
        { item_ids: itemIds },
        { headers: { 'session-token': sessionToken } }
      );
      return response.data;
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['cartItems'] });
      queryClient.invalidateQueries({ queryKey: ['cartSummary'] });
      toast.success('Item added to cart');
    },
    onError: (error: any) => {
      const message = error.response?.data?.Message || 'Failed to add item to cart';
      toast.error(message);
    },
  });
}

export function useRemoveCartItem() {
  const queryClient = useQueryClient();
  const sessionToken = localStorage.getItem('session_token');

  return useMutation({
    mutationFn: async (itemId: number) => {
      await axios.post(
        `${config.backend_base_url}cart/remove`,
        { item_ids: [itemId] },
        { headers: { 'session-token': sessionToken } }
      );
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['cartItems'] });
      queryClient.invalidateQueries({ queryKey: ['cartSummary'] });
      toast.success('Item removed from cart');
    },
    onError: () => {
      toast.error('Failed to remove item from cart');
    },
  });
}

export function useRemoveMultipleCartItems() {
  const queryClient = useQueryClient();
  const sessionToken = localStorage.getItem('session_token');

  return useMutation({
    mutationFn: async (itemIds: number[]) => {
      await axios.post(
        `${config.backend_base_url}cart/remove`,
        { item_ids: itemIds },
        { headers: { 'session-token': sessionToken } }
      );
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['cartItems'] });
      queryClient.invalidateQueries({ queryKey: ['cartSummary'] });
      toast.success('Items removed from cart');
    },
    onError: () => {
      toast.error('Failed to remove items from cart');
    },
  });
}

export function usePlaceOrder() {
  const queryClient = useQueryClient();
  const sessionToken = localStorage.getItem('session_token');

  return useMutation({
    mutationFn: async (orderRequest: CartOrderRequest) => {
      const response = await axios.post(
        `${config.backend_base_url}ml/drop/orders/create`,
        orderRequest,
        { headers: { 'session-token': sessionToken } }
      );
      return response.data;
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['cartItems'] });
      queryClient.invalidateQueries({ queryKey: ['cartSummary'] });
      toast.success('Order placed successfully');
    },
    onError: (error: any) => {
      const message = error.response?.data?.Message || 'Failed to place order';
      toast.error(message);
    },
  });
}

export function useSearchSimilarItems() {
  const sessionToken = localStorage.getItem('session_token');

  return useMutation({
    mutationFn: async (searchTerm: string) => {
      const response = await axios.post(
        `${config.backend_base_url}public/pss/search`,
        {
          search_term: searchTerm,
          limit: 100,
        },
        { headers: { 'session-token': sessionToken } }
      );
      return response.data || [];
    },
    onError: (error: any) => {
      const message = error.response?.data?.Message || 'Failed to search for similar items';
      toast.error(message);
    },
  });
}
